/**
 * Checkout Service
 *
 * Client-side service for server-side checkout processing.
 * Provides a simple interface to the secure checkout API.
 */

import type { ICheckoutRequest, ICheckoutResponse, IOrderDeliveryAddressComponents, IOrderPreview } from '@order-up/models';
import { CheckoutResponseSchema, OrderPreviewSchema } from '@order-up/schemas';
import { createApiClient } from './apiClient';
import { createLogger } from '../utils/logger';

const logger = createLogger('CheckoutAPI');

const apiClient = createApiClient('/api/v1/orders');
// Public app: use Bearer tokens only, not session cookies
apiClient.defaults.withCredentials = false;

/**
 * Preview order totals before checkout
 * Gets exact amounts from server (single source of truth)
 *
 * @param cartId - Cart ID to preview
 * @param tipAmount - Tip amount to include in preview (defaults to 0)
 * @returns Validated order preview with calculated totals
 * @throws {Error} If API response doesn't match OrderPreviewSchema
 */
const previewOrder = async (
    cartId: number,
    tipAmount: number = 0,
    redeemPoints: number = 0,
    discountCode?: string
): Promise<IOrderPreview> => {
    const trimmedCode = discountCode?.trim();
    logger.debug('Fetching order preview', {
        cartId,
        tipAmount,
        redeemPoints,
        discountCode: trimmedCode || undefined
    });

    // Note: apiClient interceptor returns response.data directly
    // `redeemPoints` is the loyalty REDEMPTION INTENT (B7) — points, not money.
    // `discountCode` is the coupon/promo REDEMPTION INTENT — the code, not money.
    // Each is only sent when present so the request is byte-identical to the
    // pre-loyalty / pre-coupon shape otherwise. The server derives the discounts
    // and echoes `loyaltyDiscount` / `couponDiscount` (or `couponError`).
    const rawResponse = await apiClient.post('/preview', {
        cartId,
        tipAmount,
        ...(redeemPoints > 0 ? { redeemPoints } : {}),
        ...(trimmedCode ? { discountCode: trimmedCode } : {})
    });

    // Validate response against schema for runtime type safety
    const response = OrderPreviewSchema.parse(rawResponse);

    logger.info('Preview fetched and validated', {
        subtotal: response.subtotal,
        tax: response.tax,
        total: response.total,
        taxBreakdownItems: response.taxBreakdown.length
    });

    return response;
};

/**
 * Process checkout on the server
 * This handles everything: order creation, payment, and cart cleanup
 *
 * @param request - Checkout request with customer info, payment details, and cart
 * @param idempotencyKey - Unique key to prevent duplicate charges
 * @returns Validated checkout response with order details
 * @throws {Error} If API response doesn't match CheckoutResponseSchema
 */
const processCheckout = async (
    request: ICheckoutRequest,
    idempotencyKey: string
): Promise<ICheckoutResponse> => {
    logger.debug('Processing checkout', {
        cartId: request.cartId,
        idempotencyKey
    });

    // Note: apiClient interceptor returns response.data directly
    const rawResponse = await apiClient.post('/checkout', request, {
        headers: {
            'idempotency-key': idempotencyKey
        }
    });

    // Validate response against schema for runtime type safety
    const response = CheckoutResponseSchema.parse(rawResponse);

    logger.info('Checkout successful and validated', {
        orderGuid: response.orderGuid,
        orderNumber: response.orderNumber,
        transactionRef: response.transactionRef
    });

    return response;
};

/**
 * Delivery coverage preflight check.
 *
 * Calls `POST /api/v1/orders/delivery-coverage` — authenticated via the same
 * Firebase Bearer token as /preview. Returns whether the courier can deliver
 * to the given address for the given restaurant, along with the live fee and
 * ETA when deliverable.
 *
 * Call this at address-entry time so unserviceable addresses are surfaced
 * before the customer adds items and proceeds to checkout.
 */
export interface DeliveryCoverageResult {
    /** True when the courier can deliver to this address. */
    deliverable: boolean;
    /** Live courier fee in cents (present when deliverable=true and Drive is configured). */
    feeCents?: number;
    /** Estimated minutes to deliver (present when deliverable=true and courier provides an ETA). */
    etaMinutes?: number;
    /** Machine-readable reason when deliverable=false (e.g. 'delivery_address_not_in_coverage'). */
    reason?: string;
    /** Human-readable message for the end customer when deliverable=false. */
    message?: string;
}

const checkDeliveryCoverage = async (params: {
    restaurantId: number;
    tenantId: number;
    deliveryAddressComponents: IOrderDeliveryAddressComponents;
    orderValueCents?: number;
}): Promise<DeliveryCoverageResult> => {
    logger.debug('Checking delivery coverage', params);
    // Note: apiClient interceptor returns response.data directly (not AxiosResponse).
    const response = await apiClient.post('/delivery-coverage', params);
    return response as unknown as DeliveryCoverageResult;
};

export const checkoutService = {
    previewOrder,
    processCheckout,
    checkDeliveryCoverage,
};

